import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ModalHelperService } from 'src/app/services/modal-helper';
import { StorageService } from 'src/app/services/storage.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-elevated-transport',
  templateUrl: './elevated-transport.component.html',
  styleUrls: ['./elevated-transport.component.scss'],
  standalone: false,
})
export class ElevatedTransportComponent implements OnInit {
  eMaxHeightDiff: any;
  projectData: any = null;
  isFormValid = true;
  selectedOption: string = "airplane_with_pressure";

  constructor(private modalController: ModalController, private storageService: StorageService,
    private modalHelper: ModalHelperService, private toastService: ToastService, private translate: TranslateService) { }

  ngOnInit() {
    this.loadProjectData();
    this.loadSavedTransportData();
  }

  ionViewWillEnter() {
    this.loadProjectData();
  }

  async loadProjectData() {
    try {
      const res = await this.storageService.getProject();
      this.projectData = res ? JSON.parse(res) : null;
      console.log('Loaded project data:', this.projectData);
    } catch (error) {
      console.error('Error loading project data:', error);
    }
  }

  async loadSavedTransportData() {
    try {
      const projectData = await this.storageService.getProject();
      if (projectData) {
        const project = JSON.parse(projectData);
        if (project.elevatedTransport) {
          this.selectedOption = project.elevatedTransport.transport || 'airplane_with_pressure';
          this.eMaxHeightDiff = project.elevatedTransport.maxHeightDiff || null;
          this.validateForm();
        }
      }
    } catch (error) {
      console.error('Error loading transport data:', error);
    }
  }

  get requiresHeightInput(): boolean {
    return this.selectedOption !== 'airplane_with_pressure';
  }

  // Get translation key for transport
  getTransportTranslationKey(): string {
    // Map internal keys to translation keys
    const keyMap: { [key: string]: string } = {
      'airplane_with_pressure': 'airplane_with_pressure',
      'helicopter': 'helicopter',
      'lorry': 'lorry',
      'cable_car': 'cable_car'
    };
    return keyMap[this.selectedOption] || this.selectedOption;
  }
  // Get translated transport label for display
  async getTransportLabel(): Promise<string> {
    const translationKey = this.getTransportTranslationKey();
    return await this.translate.get(translationKey).toPromise();
  }

  selectOption(option: string) {
    this.selectedOption = option;

    // Reset height diff when switching to airplane
    if (!this.requiresHeightInput) {
      this.eMaxHeightDiff = null;
    }

    this.validateForm();
  }

  onHeightChange() {
    this.validateForm();
  }

  validateForm(): void {
    if (!this.requiresHeightInput) {
      // Airplane doesn't need height input
      this.isFormValid = true;
    } else {
      // Other transports require valid height input
      this.isFormValid =
        this.eMaxHeightDiff !== null &&
        this.eMaxHeightDiff !== undefined
    }
  }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }

  async next() {
    if (!this.isFormValid) {
      await this.toastService.show('Please enter valid height difference');
      return;
    }
    if (this.requiresHeightInput && (this.eMaxHeightDiff < 100 || this.eMaxHeightDiff > 5100)) {
      await this.toastService.show(this.translate.instant('MAX_HEIGHT_DIFFERENCE_INVALID_MESSAGE'), 3000, 'bottom');
      return;
    }
    const translatedLabel = await this.getTransportLabel();
    // Prepare data to save
    const transportData = {
      transport: this.selectedOption,
      transportKey: this.getTransportTranslationKey(), // Store translation key
      transportLabel: translatedLabel, // Store current translated label
      maxHeightDiff: this.requiresHeightInput ? this.eMaxHeightDiff : null
    };
    try {
      // Save to storage
      const projectData = await this.storageService.getProject();
      const project = projectData ? JSON.parse(projectData) : {};

      if (!project.elevatedTransport) {
        project.elevatedTransport = {};
      }

      project.elevatedTransport.transport = transportData.transport;
      project.elevatedTransport.transportKey = transportData.transportKey;
      project.elevatedTransport.transportLabel = transportData.transportLabel;
      project.elevatedTransport.maxHeightDiff = transportData.maxHeightDiff;

      await this.storageService.updateProject({
        elevatedTransport: project.elevatedTransport
      });

      console.log('Transport data saved:', transportData);

      // Dismiss modal with data
      await this.modalController.dismiss(transportData, 'confirm');

    } catch (error) {
      console.error('Error saving transport data:', error);
    }
  }


}
